import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Scissors, X, ArrowUp, ArrowDown, Loader2, Download, Film, Type, Smartphone, Monitor, Square, Music4, GripVertical } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const ASPECTS = [
  { v: "native", label: "Original", icon: Film },
  { v: "9:16", label: "9:16 Reels", icon: Smartphone },
  { v: "16:9", label: "16:9 YouTube", icon: Monitor },
  { v: "1:1", label: "1:1 Feed", icon: Square },
];
const QUALITIES = ["HD 720p", "Full HD 1080p", "4K"];

export function EditorBody({ project, songs = [], onSaved }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aspect, setAspect] = useState("native");
  const [quality, setQuality] = useState("Full HD 1080p");
  const [card, setCard] = useState({ enabled: false, title: project?.title || "", subtitle: "Metáfora Play", seconds: 3, template: "clasico" });
  const [intro, setIntro] = useState({ enabled: false, title: project?.title || "", subtitle: "", seconds: 3, template: "cine" });
  const [soundtrack, setSoundtrack] = useState("");
  const [musicFade, setMusicFade] = useState(true);
  const [transition, setTransition] = useState("none");
  const [saveNew, setSaveNew] = useState(false);
  const [newTitle, setNewTitle] = useState(`${project?.title || "Edición"} (edición)`);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setResult(null);
    (async () => {
      try {
        const r = await api.get(`/projects/${project.id}/timeline`);
        if (!alive) return;
        setClips((r.data.clips || []).map((c) => ({ ...c, include: true, start: 0, end: c.duration || 0, text: "" })));
      } catch { toast.error("No se pudieron cargar los clips"); }
      finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, [project.id]);

  const move = (i, dir) => {
    setClips((cs) => {
      const j = i + dir;
      if (j < 0 || j >= cs.length) return cs;
      const n = [...cs];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  };
  const [dragIdx, setDragIdx] = useState(null);
  const reorder = (from, to) => {
    if (from === null || to === null || from === to) return;
    setClips((cs) => {
      const n = [...cs];
      const [item] = n.splice(from, 1);
      n.splice(to, 0, item);
      return n;
    });
  };
  const patch = (i, f) => setClips((cs) => cs.map((c, k) => (k === i ? { ...c, ...f } : c)));

  const totalSecs = clips.filter((c) => c.include).reduce((s, c) => s + Math.max(0, (c.end || 0) - (c.start || 0)), 0)
    + (card.enabled ? Number(card.seconds || 0) : 0);

  const generate = async () => {
    const segments = clips.filter((c) => c.include).map((c) => ({ video_path: c.video_path, start: Number(c.start || 0), end: Number(c.end || 0), text: c.text || "" }));
    if (!segments.length) { toast.error("Incluye al menos un clip"); return; }
    setBusy(true);
    setResult(null);
    try {
      const body = { segments, aspect, quality, transition };
      if (card.enabled) body.end_card = { title: card.title, subtitle: card.subtitle, seconds: Number(card.seconds || 3), template: card.template };
      if (intro.enabled) body.intro_card = { title: intro.title, subtitle: intro.subtitle, seconds: Number(intro.seconds || 3), template: intro.template };
      if (soundtrack) { body.soundtrack_project_id = soundtrack; body.music_fade = musicFade; }
      if (saveNew) { body.save_as_new = true; body.new_title = newTitle; }
      const r = await api.post(`/projects/${project.id}/editor`, body);
      setResult(r.data.download_path);
      toast.success(r.data.new_project_id ? "¡Guardado como proyecto nuevo en tu historial!" : "¡Edición lista!");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la edición"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="py-10 text-center text-[#64748B] flex items-center gap-2 w-full justify-center"><Loader2 className="w-5 h-5 spin" /> Cargando clips…</div>;
  if (clips.length === 0) return <div className="py-10 text-center text-[#64748B]">No hay clips renderizados para editar todavía.</div>;

  return (
    <>
      <div className="space-y-2 mb-4" data-testid="editor-clips">
        {clips.map((c, i) => (
          <div key={c.video_path} draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { reorder(dragIdx, i); setDragIdx(null); }}
            onDragEnd={() => setDragIdx(null)}
            className={`rounded-xl border p-3 transition-opacity ${c.include ? "border-white/12 bg-[#0B0C13]" : "border-white/6 bg-[#0B0C13]/40 opacity-60"} ${dragIdx === i ? "opacity-40 ring-2 ring-[#FF334B]" : ""}`} data-testid={`editor-clip-${i}`}>
            <div className="flex items-center gap-3">
              <GripVertical className="w-4 h-4 text-[#64748B] cursor-grab shrink-0" data-testid={`editor-drag-${i}`} />
              <video src={mediaUrl(c.video_path)} muted className="w-24 aspect-video object-cover rounded-lg bg-black shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-head text-sm font-semibold">{c.label} <span className="text-[11px] text-[#64748B] font-normal">· {c.duration}s</span></div>
                {c.raw && <div className="text-[11px] text-[#64748B] truncate">{c.raw}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`editor-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid={`editor-down-${i}`} onClick={() => move(i, 1)} disabled={i === clips.length - 1} className="text-[#64748B] hover:text-white disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] shrink-0 cursor-pointer">
                <input type="checkbox" data-testid={`editor-include-${i}`} checked={c.include} onChange={(e) => patch(i, { include: e.target.checked })} className="accent-[#FF334B]" /> Incluir
              </label>
            </div>
            {c.include && (
              <div className="mt-2 space-y-1.5" data-testid={`editor-trim-${i}`}>
                <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                  <span className="w-10 shrink-0">Inicio</span>
                  <input type="range" min="0" max={c.duration} step="0.1" value={c.start}
                    onChange={(e) => patch(i, { start: Math.min(Number(e.target.value), c.end - 0.2) })}
                    className="flex-1 min-w-0 accent-[#00F0FF]" data-testid={`editor-start-${i}`} />
                  <span className="w-9 text-right shrink-0">{Number(c.start).toFixed(1)}s</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                  <span className="w-10 shrink-0">Fin</span>
                  <input type="range" min="0" max={c.duration} step="0.1" value={c.end}
                    onChange={(e) => patch(i, { end: Math.max(Number(e.target.value), c.start + 0.2) })}
                    className="flex-1 min-w-0 accent-[#FF334B]" data-testid={`editor-end-${i}`} />
                  <span className="w-9 text-right shrink-0">{Number(c.end).toFixed(1)}s</span>
                </div>
                <input data-testid={`editor-text-${i}`} value={c.text} onChange={(e) => patch(i, { text: e.target.value })}
                  placeholder="Texto en pantalla (opcional, ej. título de la escena)"
                  className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-[#00F0FF]" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-white/10 p-3 mb-3" data-testid="editor-intro">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
          <input type="checkbox" data-testid="editor-intro-toggle" checked={intro.enabled} onChange={(e) => setIntro((s) => ({ ...s, enabled: e.target.checked }))} className="accent-[#00F0FF]" />
          <Type className="w-4 h-4 text-[#00F0FF]" /> Intro animada (tarjeta de apertura)
        </label>
        {intro.enabled && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input data-testid="editor-intro-title" placeholder="Título" value={intro.title} onChange={(e) => setIntro((s) => ({ ...s, title: e.target.value }))} className="col-span-2 bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00F0FF]" />
            <input data-testid="editor-intro-subtitle" placeholder="Subtítulo" value={intro.subtitle} onChange={(e) => setIntro((s) => ({ ...s, subtitle: e.target.value }))} className="bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00F0FF]" />
            <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
              <span>Duración</span>
              <input type="number" min="1" max="10" step="0.5" data-testid="editor-intro-seconds" value={intro.seconds} onChange={(e) => setIntro((s) => ({ ...s, seconds: e.target.value }))} className="w-16 bg-[#0B0C13] border border-white/10 rounded-lg px-2 py-1.5 outline-none focus:border-[#00F0FF]" />
              <span>s</span>
            </div>
            <div className="col-span-2 flex flex-wrap gap-2 pt-1" data-testid="editor-intro-templates">
              {[["clasico", "Clásico"], ["minimal", "Minimal"], ["marca", "Con marca"], ["cine", "Cine"]].map(([v, l]) => (
                <button key={v} data-testid={`editor-intro-template-${v}`} onClick={() => setIntro((s) => ({ ...s, template: v }))}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${intro.template === v ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/12 text-[#94A3B8] hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 p-3 mb-3" data-testid="editor-transition">
        <div className="mono-label mb-2 inline-flex items-center gap-1.5"><Film className="w-3.5 h-3.5 text-[#FF9F2E]" /> Transición entre clips</div>
        <div className="flex flex-wrap gap-2">
          {[["none", "Corte"], ["fade", "Fundido"], ["dissolve", "Disolver"], ["wipe", "Barrido"], ["slide", "Deslizar"]].map(([v, l]) => (
            <button key={v} data-testid={`editor-transition-${v}`} onClick={() => setTransition(v)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${transition === v ? "border-[#FF9F2E] text-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 text-[#94A3B8] hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        {transition !== "none" && (
          <div className="mt-3 flex items-center gap-3" data-testid="editor-transition-preview">
            <div className="relative w-40 aspect-video rounded-lg overflow-hidden bg-black shrink-0 border border-white/10">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00F0FF]/80 to-[#0B0C13] flex items-center justify-center text-[10px] font-bold text-white/70">CLIP A</div>
              <div key={transition} className={`absolute inset-0 bg-gradient-to-br from-[#FF334B] to-[#FF9F2E] flex items-center justify-center text-[10px] font-bold text-white tp-${transition}`}>CLIP B</div>
            </div>
            <p className="text-[11px] text-[#64748B]">Vista previa aproximada del efecto entre un clip y el siguiente.</p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 p-3 mb-3" data-testid="editor-endcard">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
          <input type="checkbox" data-testid="editor-card-toggle" checked={card.enabled} onChange={(e) => setCard((s) => ({ ...s, enabled: e.target.checked }))} className="accent-[#FF9F2E]" />
          <Type className="w-4 h-4 text-[#FF9F2E]" /> Tarjeta final
        </label>
        {card.enabled && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input data-testid="editor-card-title" placeholder="Título" value={card.title} onChange={(e) => setCard((s) => ({ ...s, title: e.target.value }))} className="col-span-2 bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF9F2E]" />
            <input data-testid="editor-card-subtitle" placeholder="Subtítulo" value={card.subtitle} onChange={(e) => setCard((s) => ({ ...s, subtitle: e.target.value }))} className="bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF9F2E]" />
            <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
              <span>Duración</span>
              <input type="number" min="1" max="10" step="0.5" data-testid="editor-card-seconds" value={card.seconds} onChange={(e) => setCard((s) => ({ ...s, seconds: e.target.value }))} className="w-16 bg-[#0B0C13] border border-white/10 rounded-lg px-2 py-1.5 outline-none focus:border-[#FF9F2E]" />
              <span>s</span>
            </div>
            <div className="col-span-2 flex flex-wrap gap-2 pt-1" data-testid="editor-card-templates">
              {[["clasico", "Clásico"], ["minimal", "Minimal"], ["marca", "Con marca"], ["cine", "Cine"]].map(([v, l]) => (
                <button key={v} data-testid={`editor-card-template-${v}`} onClick={() => setCard((s) => ({ ...s, template: v }))}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${card.template === v ? "border-[#FF9F2E] text-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 text-[#94A3B8] hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {songs.length > 0 && (
        <div className="mb-3" data-testid="editor-soundtrack">
          <div className="mono-label mb-1.5 inline-flex items-center gap-1.5"><Music4 className="w-3.5 h-3.5 text-[#00F0FF]" /> Banda sonora (opcional)</div>
          <select data-testid="editor-soundtrack-select" value={soundtrack} onChange={(e) => setSoundtrack(e.target.value)} className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#00F0FF]">
            <option value="">Sin música (audio original)</option>
            {songs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          {soundtrack && (
            <label className="flex items-center gap-2 mt-2 text-xs text-[#94A3B8] cursor-pointer" data-testid="editor-music-fade-label">
              <input type="checkbox" data-testid="editor-music-fade" checked={musicFade} onChange={(e) => setMusicFade(e.target.checked)} className="accent-[#00F0FF]" />
              Fundido de entrada y salida en la música
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-3" data-testid="editor-aspect">
        {ASPECTS.map((a) => {
          const A = a.icon;
          return (
            <button key={a.v} data-testid={`editor-aspect-${a.v}`} onClick={() => setAspect(a.v)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border transition-colors ${aspect === a.v ? "border-[#FF334B] text-[#FF334B] bg-[#FF334B]/10" : "border-white/12 text-[#94A3B8] hover:text-white"}`}>
              <A className="w-3.5 h-3.5" /> {a.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="editor-quality">
        <span className="mono-label">Calidad:</span>
        {QUALITIES.map((q) => (
          <button key={q} data-testid={`editor-quality-${q.split(" ")[0]}`} onClick={() => setQuality(q)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${quality === q ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/12 text-[#94A3B8] hover:text-white"}`}>
            {q}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-[#64748B]">Duración final ≈ {totalSecs.toFixed(1)}s</span>
      </div>

      <div className="rounded-xl border border-white/10 p-3 mb-3" data-testid="editor-savenew">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
          <input type="checkbox" data-testid="editor-savenew-toggle" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} className="accent-[#FF334B]" />
          Guardar como proyecto nuevo (conserva el original)
        </label>
        {saveNew && (
          <input data-testid="editor-savenew-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Nombre del proyecto editado"
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF334B]" />
        )}
      </div>

      <button data-testid="editor-generate" onClick={generate} disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 spin" /> : <Scissors className="w-4 h-4" />} {busy ? "Ensamblando tu edición…" : "Generar edición"}
      </button>

      {result && (
        <div className="mt-4 border-t border-white/10 pt-4" data-testid="editor-result">
          <div className="mono-label mb-2">Resultado</div>
          <video src={mediaUrl(result)} controls className="w-full max-w-xs mx-auto rounded-xl bg-black" />
          <a href={mediaUrl(result)} download={`${project.title}-editado.mp4`} data-testid="editor-download"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full bg-white text-black hover:bg-white/90 transition-colors">
            <Download className="w-4 h-4" /> Descargar edición
          </a>
        </div>
      )}
    </>
  );
}

export default function VideoEditor({ project, songs = [], onClose, onSaved }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="video-editor-overlay">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto mfp-card p-6 relative" data-testid="video-editor">
        <button onClick={onClose} data-testid="editor-close" className="absolute top-4 right-4 text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 mb-1">
          <Scissors className="w-5 h-5 text-[#FF334B]" />
          <h3 className="font-head text-xl font-bold">Editor de video</h3>
        </div>
        <p className="text-sm text-[#64748B] mb-4">Recorta, reordena y une tus clips, añade una tarjeta final y exporta en el formato que quieras. La edición es gratis (se procesa localmente).</p>
        <EditorBody project={project} songs={songs} onSaved={onSaved} />
      </div>
    </div>
  );
}
